import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  feePromotionCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { feeEngineEndpoints } from "@/features/fee-engine/api/endpoints";
import type { PromotionDto } from "@/features/fee-engine/api/dto";

/**
 * Crée une promotion (`POST /Promotions/add`, guide §6.5) — remise sur
 * les frais avec quota global et quota par client.
 */
export const useCreatePromotion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["fee-engine", "promotions", "create"],
    mutationFn: (body: PromotionDto) =>
      mutateData({
        url: feeEngineEndpoints.promotions.add,
        reqBody: body as unknown as Record<string, unknown>,
        method: "POST",
      }) as Promise<PromotionDto>,
    onSuccess: () => {
      return invalidateAfterMutation(queryClient, feePromotionCacheKeys(), {
        resetListPage: true,
      });
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useCreatePromotion failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "fee-engine" }
      );
    },
  });
};
